import { useState, useEffect } from 'react'
import { Loader2, AlertCircle, Download, Filter, ArrowLeft, LogOut } from 'lucide-react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { useAuth } from '@/hooks/useAuth'
import type { Lead } from '@/types'

export default function AdminLeads() {
  const { user, loading: authLoading, signInWithGoogle, signOut, getIdToken } = useAuth()
  const [leads, setLeads] = useState<Lead[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [signInError, setSignInError] = useState<string | null>(null)

  // Clean up old token-based auth from localStorage
  useEffect(() => {
    localStorage.removeItem('admin_token')
  }, [])

  const handleSignIn = async () => {
    try {
      setSignInError(null)
      await signInWithGoogle()
    } catch (err) {
      setSignInError(err instanceof Error ? err.message : 'Sign-in failed')
    }
  }

  useEffect(() => {
    if (!user) return

    async function fetchLeads() {
      setLoading(true)
      setError(null)
      try {
        const idToken = await getIdToken()
        const params = typeFilter !== 'all' ? `?type=${typeFilter}` : ''
        const response = await fetch(`/api/leads/admin${params}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
        if (response.status === 401 || response.status === 403) {
          const data = await response.json()
          throw new Error(data.error || 'Access denied')
        }
        if (!response.ok) throw new Error('Failed to fetch leads')
        const data = await response.json()
        setLeads(data.leads)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setLoading(false)
      }
    }

    fetchLeads()
  }, [user, typeFilter, getIdToken])

  const handleExport = async () => {
    try {
      const idToken = await getIdToken()
      const response = await fetch('/api/leads/admin/export', {
        headers: { Authorization: `Bearer ${idToken}` },
      })
      if (!response.ok) throw new Error('Export failed')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'spirit-charsoft-leads.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
    }
  }

  // Loading spinner while Firebase restores persisted session
  if (authLoading) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-mid" />
      </div>
    )
  }

  // Sign-in screen
  if (!user) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center px-6">
        <div className="bg-white rounded-2xl border border-card-border p-8 w-full max-w-sm shadow-sm text-center">
          <h1 className="font-display text-2xl font-bold text-text-dark mb-2">Admin Access</h1>
          <p className="text-text-gray text-sm mb-6">Sign in with your authorized Google account.</p>
          {signInError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
              {signInError}
            </div>
          )}
          <button
            onClick={handleSignIn}
            className="w-full py-3 px-4 rounded-lg bg-brand-dark text-white font-semibold transition-transform duration-150 hover:scale-[1.01] flex items-center justify-center gap-3"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    )
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-bg-main">
      {/* Header */}
      <div className="bg-white border-b border-card-border px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-text-gray hover:text-text-dark transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-display text-xl font-bold text-text-dark">Leads</h1>
            <Link
              to="/admin/resources"
              className="text-sm text-brand-mid hover:text-brand-dark transition-colors"
            >
              Resources
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-text-gray hidden sm:inline">{user.email}</span>
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 py-2 px-4 rounded-lg bg-brand-dark text-white text-sm font-semibold transition-transform duration-150 hover:scale-[1.02]"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={signOut}
              className="inline-flex items-center gap-2 py-2 px-3 rounded-lg border border-card-border text-text-gray text-sm hover:text-text-dark transition-colors"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <Filter className="w-4 h-4 text-text-gray" />
          {['all', 'lead', 'subscriber', 'waitlist'].map((type) => (
            <button
              key={type}
              onClick={() => setTypeFilter(type)}
              className={clsx(
                'py-1.5 px-4 rounded-full text-sm font-medium transition-colors',
                typeFilter === type
                  ? 'bg-brand-dark text-white'
                  : 'bg-white text-text-gray border border-card-border hover:border-brand-mid'
              )}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-brand-mid" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-red-500 shrink-0" />
            <div>
              <h3 className="font-semibold text-red-800">Error</h3>
              <p className="text-red-700 mt-1">{error}</p>
            </div>
          </div>
        ) : leads.length === 0 ? (
          <div className="bg-white rounded-xl border border-card-border p-12 text-center">
            <div className="text-5xl mb-4">📭</div>
            <h3 className="text-lg font-semibold text-text-dark mb-2">No leads yet</h3>
            <p className="text-text-gray">Leads will appear here when visitors submit their email on the landing page.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-card-border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-gray uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-gray uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-gray uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-gray uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-text-dark">{lead.email}</td>
                    <td className="px-6 py-4 text-sm text-text-gray">{lead.source}</td>
                    <td className="px-6 py-4">
                      <span
                        className={clsx(
                          'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                          lead.type === 'lead' && 'bg-blue-100 text-blue-700',
                          lead.type === 'subscriber' && 'bg-green-100 text-green-700',
                          lead.type === 'waitlist' && 'bg-amber-100 text-amber-700',
                        )}
                      >
                        {lead.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-text-gray">
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
